import _ from 'lodash';
import gql from 'graphql-tag';
import { OrderDiscountOffer, NewDiscountType } from '@baemingo/offer';

import { LocalItem, i18nProps, CartItem, LocalVariation } from '~Types';
import { useI18n } from '~Hooks';

import { useEntryFinder } from '~Components/EntryFinder';
import { useMemo } from 'react';
import { DISCOUNT_TYPE } from '~../__queries__';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { calculateOfferValues, groupOfferMatches } from './offers';
import useOffers from '~Hooks/useOffers';
import { GetOffers_Company_offers } from '~Hooks/__queries__';

interface VenueDiscountExclusionFragment {
  id: string;
  discountExcludedCategoryIds: string[] | null;
}

/** Reads discountExcludedCategoryIds from Apollo cache for the given venue */
export function getDiscountExcludedCategoryIds(
  venueId: string,
  client: ApolloClient<any>,
): string[] {
  try {
    const venue = client.readFragment<VenueDiscountExclusionFragment>({
      id: venueId,
      fragment: gql`
        fragment VenueDiscountExclusionFragment on Venue {
          id
          discountExcludedCategoryIds
        }
      `,
    });
    return venue?.discountExcludedCategoryIds ?? [];
  } catch {
    return [];
  }
}

export function packageItems(
  items: LocalItem[],
  venueId: string,
  discountRate: number | null,
  discountType: DISCOUNT_TYPE | null,
  findEntryFromItem: (itemId: string) => any,
  client: ApolloClient<object>,
  i18n: i18nProps,
  noVariationSum?: boolean,
  discountItemIds?: string[],
  isMembershipDiscountEnabled?: boolean,
  offers?: GetOffers_Company_offers[],
  discountId?: string,
): CartItem[] {
  const grouped = groupOfferMatches(items, venueId, client, i18n);

  const values = calculateOfferValues(
    grouped.matches.map((match) => ({
      offerId: match.offerId,
      entries: match.items.map(({ id }) =>
        findEntryFromItem(items!.find((i) => i.id === id)!.itemId),
      ),
    })),
    venueId,
    client,
  );

  // Gets entryId for variations as item
  const mapVariation = (variation: LocalVariation) => {
    if (!variation.asItem) {
      return variation;
    }

    const variationAsItemEntry = findEntryFromItem(variation.asItem.id);
    if (!variationAsItemEntry) {
      return variation;
    }

    return {
      ...variation,
      asItem: {
        ...variation.asItem,
        entryId: variationAsItemEntry.id,
      },
    };
  };

  const offerItems = grouped.matches.reduce((arr, match, index) => {
    match.items.forEach(({ id, variations }) => {
      const item = items.find((i) => i.id === id)!;
      const entry = findEntryFromItem(item.itemId)!;
      const offerItem = values![index].find((v) => v.id === entry.id)!;

      const fraction = item.fraction ?? 1;
      const instant = entry.instant ?? false;

      const variationsSum = !noVariationSum ? _.sum(variations.map((v) => v.price)) : 0;
      const price = offerItem.originalPrice + variationsSum;
      const discount = (offerItem.originalPrice - offerItem.discountedPrice) * fraction;
      const amount = price * fraction;

      arr.push({
        ...item,
        variations: item.variations.map(mapVariation),
        entryId: entry.id,
        offerId: match.offerId,
        amount,
        originalAmount: (entry.price + variationsSum) * fraction,
        fraction,
        instant: false,
        discount: discount,
        discountTitle: item.discountTitle,
        vatRate: item.vatRate,
        takeAwayVatRate: item.takeAwayVatRate,
        count: 1,
      });
    });

    return arr;
  }, [] as CartItem[]);

  let normalItems: Array<CartItem> = grouped.unmatched.map(
    (item): CartItem => {
      const entry = findEntryFromItem(item.itemId);

      const variationsSum = !noVariationSum ? _.sum(item.variations.map((v) => v.price)) : 0;

      if (!entry && !item.entryId) {
        throw new Error('Item missing in menu');
      }

      if (!entry && !item.price && !item.amount) {
        throw new Error('Item missing price');
      }

      const price = (item.price || item.amount || entry?.price) + variationsSum;

      const amount = item.fraction ? price * item.fraction : price;

      return {
        ...item,
        variations: item.variations.map(mapVariation),
        amount,
        fraction: item.fraction || 1,
        instant: false,
        discount: item.discount || 0,
        discountTitle: item.discountTitle,
        entryId: item.entryId || entry.id,
        vatRate: item.vatRate,
        takeAwayVatRate: item.takeAwayVatRate,
        count: 1,
      };
    },
  );

  if (discountRate !== null && discountType) {
    const excludedCategoryIds = getDiscountExcludedCategoryIds(venueId, client);
    const rawOffer = offers ? offers.find((o) => o.id === discountId) : null;

    // Map old DISCOUNT_TYPE to new NewDiscountType
    const discountTypeMap: Record<DISCOUNT_TYPE, NewDiscountType> = {
      [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
      [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
      [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
    };

    // Use new OrderDiscountOffer API
    const offer = new OrderDiscountOffer({
      discountType: discountTypeMap[discountType],
      discountRate: discountRate,
      allowedCategoryIds: rawOffer ? rawOffer.categories.map((c) => c.id) : [],
      allowedItemIds: rawOffer ? rawOffer.items.map((i) => i.id) : [],
    });

    const itemsExcludedByOffer: CartItem[] = [];

    normalItems.forEach((item) => {
      if (
        discountItemIds &&
        discountItemIds.length > 0 &&
        !discountItemIds?.includes(item.itemId)
      ) {
        itemsExcludedByOffer.push(item);
        return;
      }

      if (isMembershipDiscountEnabled && item.disableMembershipDiscount) {
        itemsExcludedByOffer.push(item);
        return;
      }

      // Skip items in venue-level discount excluded categories
      if (excludedCategoryIds.includes(item.categoryId)) {
        itemsExcludedByOffer.push(item);
        return;
      }

      offer.addItem({
        ...item,
        id: item.id,
        itemId: item.itemId,
        categoryId: item.categoryId,
        amount: item.amount,
      });
    });

    const { changedItems, unchangedItems } = offer.calculate();

    const formattedChangedItems = changedItems.map((changedItem) => {
      const fullItem = normalItems.find((ni) => ni.id === changedItem.id)!;
      const hasDiscount = fullItem.discount !== 0;

      const result = {
        ...fullItem,
        // If we have set a discount on this item then that has priority
        discount: hasDiscount ? fullItem.discount : changedItem.discount,
        // Set discount title from the offer if not already set
        discountTitle:
          fullItem.discountTitle ||
          (rawOffer?.name ?? undefined) ||
          (i18n('Discount.Manual') as string),
      };

      return result;
    });

    const formattedUnchangedItems = unchangedItems.map((unchangedItem) => {
      const fullItem = normalItems.find((ni) => ni.id === unchangedItem.id)!;
      return {
        ...fullItem,
      };
    });

    normalItems = [...formattedChangedItems, ...formattedUnchangedItems, ...itemsExcludedByOffer];
  }

  return offerItems.concat(normalItems);
}

export function useItemsPackager(
  items: LocalItem[],
  venueId: string,
  discountRate: number | null,
  discountType: DISCOUNT_TYPE | null,
  noVariationSum?: boolean,
  discountItemIds?: string[],
  isMembershipDiscountEnabled?: boolean,
  discountId?: string,
): CartItem[] {
  const { i18n } = useI18n();

  const findEntryFromItem = useEntryFinder();
  const client = useApolloClient();
  const { offers } = useOffers();

  return useMemo(
    () =>
      packageItems(
        items,
        venueId,
        discountRate,
        discountType,
        findEntryFromItem,
        client,
        i18n,
        noVariationSum,
        discountItemIds,
        isMembershipDiscountEnabled,
        offers,
        discountId,
      ),
    [items, venueId, discountRate, discountType, discountItemIds, discountId],
  );
}
